import { Box, Button, Card, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography, useTheme } from '@mui/material'
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import LogsTableRow from '../../../components/admin/logging/LogsTableRow'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BlockMessage from '../../../components/common/BlockMessage'
import TableHeader from '../../../components/common/TableHeader'
import { Icons } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import noLogsImg from '../../../assets/no-logs.svg'
import { useSearchParams } from 'react-router-dom'
import api from '../../../service/api'
import FilterBar from '../../../components/admin/logging/FilterBar'

const tableColumns = [
    { name: "Service", padding: "5px 10px" },
    { name: "Level", minWidth: "100px", padding: "5px 10px" },
    { name: "time", padding: "5px 10px" },
    { name: "Type", padding: "5px 10px" },
    { name: "Message", minWidth: 500, padding: "5px 10px" },
]

const DebugLogs = () => {

    const [page, setPage] = useState( 0 )
    const [count] = useState( 100 )
    const [logs, setLogs] = useState( {} )
    const [stats, setStats] = useState( {} )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [rowsPerPage, setRowsPerPage] = useState( 100 )
    const [loadingMore, setLoadingMore] = useState( false )
    const [loadingStats, setLoadingStats] = useState( false )
    const [hasPermission, setHasPermission] = useState( true )
    const [availableModules, setAvailableModules] = useState( [] )

    const UserStore = useUserStore()

    const [searchParams, setSearchParams] = useSearchParams( { filterLevel: "error", view: "logs" } )
    const filterUserId = searchParams.get( "filterUserId" ) || ""
    const filterDate = searchParams.get( "filterDate" ) || ""
    const filterModule = searchParams.get( "filterModule" ) || ""
    const filterLevel = searchParams.get( "filterLevel" ) || ""
    const view = searchParams.get( "view" ) || "logs"

    const statsView = useMemo( () => view === 'stats', [view] )

    const filtersApplied = Boolean( filterDate || filterUserId || filterLevel || filterModule )

    const { palette, border, table, layout } = useTheme()
    const fetchedLogsCount = useRef( 0 )

    const removeFilters = () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'filterDate', '' )
            newParams.set( 'filterUserId', '' )
            newParams.set( 'filterLevel', '' )
            newParams.set( 'filterModule', '' )
            return newParams
        }, { replace: true } )
    }

    const setStatsView = useCallback( state => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'view', state ? 'stats' : 'logs' )
            return newParams
        }, { replace: true } )
    }, [setSearchParams] )

    const logFilterFunc = useCallback( item => {
        if ( item.rowType === 'date' ) return true
        return item?.meta?.module?.match( filterModule ) && item?.level?.match( filterLevel )
    }, [filterModule, filterLevel] )

    // const handleCountChange = action => {
    //     if ( action === '+' ) {
    //         if ( count <= 400 )
    //             setCount( prev => prev + 100 )
    //     } else if ( action === '-' ) {
    //         if ( count > 100 )
    //             setCount( prev => prev - 100 )
    //     }
    // }

    const filteredLogs = useMemo( () => {
        return ( logs[page] || [] ).filter( logFilterFunc )
    }, [logs, page, logFilterFunc] )

    const switchMode = async newMode => {
        if ( newMode === 'stats' ) {
            if ( !stats || Object.keys( stats ).length === 0 ) {
                await fetchStats()
            } else {
                setStatsView( true )
            }
        } else {
            setStatsView( false )
        }
    }

    const refreshData = () => {
        if ( statsView )
            fetchStats()
        else
            getData()
    }

    const fetchStats = useCallback( async () => {
        try {
            const filters = {}
            if ( !filterDate || ( filterDate && filterDate.match( /^(\d{4}-\d{2}-\d{2})$/ ) ) )
                filters.filterDate = filterDate || ""
            setLoadingStats( true )
            const { data } = await api.logging.fetchServiceLogsStats( filters )
            const nullErrors = data.counts?.findIndex( err => err._id + "" === "null" )
            if ( nullErrors >= 0 ) {
                const serverErrors = data.counts?.find( err => +err._id === 500 )
                if ( serverErrors ) {
                    serverErrors.count += data.counts[nullErrors].count
                } else {
                    data.counts.push( { _id: 500, count: nullErrors.count } )
                }
                data.counts.splice( nullErrors, 0 )
            }
            for ( const item of data.errorsByModule ) {
                item.module = String( item._id ).replace( /_/g, " " ).toLowerCase()
                const mod = String( item._id ).replace( /_/g, " " )
                const words = mod.split( " " )
                let shortCode = ''
                if ( words.length > 1 ) {
                    shortCode = words.map( w => w[0] ).join( "" )
                } else {
                    shortCode = mod.substring( 0, 4 )
                }
                item.shortCode = shortCode
            }
            setStats( data )
            setStatsView( true )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong" } )
        } finally {
            setLoadingStats( false )
        }
    }, [filterDate, setStatsView] )

    const getData = useCallback( async ( loadingMore = false ) => {
        try {
            if ( loadingMore )
                setLoadingMore( true )
            else
                setLoading( true )

            const filters = {}
            if ( filterDate && filterDate.match( /^(\d{4}-\d{2}-\d{2})$/ ) ) filters.filterDate = filterDate
            if ( filterUserId ) filters.filterUserId = filterUserId
            if ( filterLevel ) filters.filterLevel = filterLevel
            filters.count = count
            if ( loadingMore )
                filters.skipBatchCount = Math.ceil( fetchedLogsCount.current / count )


            const { data: { logs: data, uniqueModules } } = await api.logging.fetchServiceLogs( filters )
            setError( null )

            if ( loadingMore ) {
                fetchedLogsCount.current += data.length
                setLogs( prev => {
                    const newData = { ...prev }
                    newData[Math.ceil( fetchedLogsCount.current / count )] = data
                    return newData
                } )
            } else {
                fetchedLogsCount.current = data.length
                setLogs( prev => {
                    const newData = {}
                    newData[Math.ceil( fetchedLogsCount.current / count )] = data
                    return newData
                } )
            }

            setPage( Math.ceil( fetchedLogsCount.current / count ) )
            setAvailableModules( uniqueModules )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong" } )
        } finally {
            setLoading( false )
            setLoadingMore( false )
        }
    }, [filterDate, filterUserId, filterLevel, count] )

    const totalFetchedLogs = Object.values( logs ).reduce( ( acc, item ) => acc + ( item?.length || 0 ), 0 )

    useEffect( () => {
        setPage( 0 )
    }, [filterDate, filterLevel, filterModule, filterUserId] )

    useEffect( () => {
        if ( UserStore.getUser.user_role === 'SUBADMIN' && !UserStore.getUser.user_permissions.debug_logs ) {
            setHasPermission( false )
        } else {
            if ( view === 'stats' )
                fetchStats()
            else
                getData()
        }
    }, [UserStore, view, getData, fetchStats] )


    return (
        hasPermission ? (
            <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding={layout.pagePadding}>
                <Box
                    sx={{
                        background: statsView && "none",
                        minHeight: !statsView && filteredLogs.length > 0 && !loading && !error ? "450px" : "initial",
                        display: "flex",
                        flexGrow: !statsView && filteredLogs.length > 0 && !loading && !error && !statsView ? 1 : "initial",
                        height: !statsView && filteredLogs.length > 0 && !loading && !error ? "100%" : "initial",
                        flexDirection: "column",
                        gap: layout.cardsGap
                    }}>

                    <Box display="flex" flexDirection="column" sx={{ borderRadius: statsView ? "10px" : "0", flexShrink: 0, paddingBottom: statsView ? "10px" : "0", gap: layout.cardsGap }}>
                        <Box alignItems="center" gap="20px" flexWrap="wrap" display="flex" justifyContent="space-between">
                            <Box>
                                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Service Logs</Typography>
                                <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Watch all Alive services logs here</Typography>
                            </Box>
                            <Box display="flex" flexWrap="wrap" height="45px" gap="10px">
                                {!statsView && <Button size='small' disabled={loadingStats} onClick={() => switchMode( 'stats' )} variant='outlined' color='secondary' sx={{ textTransform: "capitalize" }} startIcon={loadingStats ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.AnalyticsIconOutlined} >Stats</Button>}
                                {statsView && <Button size='small' disabled={loading} onClick={() => switchMode( 'list' )} variant='outlined' color='secondary' sx={{ textTransform: "capitalize" }} startIcon={loading ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.ViewListIcon} >Logs</Button>}
                                <Button size='small' disabled={loading} onClick={refreshData} variant='outlined' color='greyed' sx={{ textTransform: "capitalize" }} startIcon={loading ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.Replay} >Refresh</Button>
                            </Box>
                        </Box>

                        {/* Filter Bar */}
                        <FilterBar statsView={statsView} disabled={loading} availableModules={availableModules} />

                        {!error && !loadingStats && statsView && stats.counts && Object.keys( stats.counts ).length > 0 && <Box justifyContent="stretch" display="flex" flexWrap="wrap" gap="20px">
                            <Box flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Info Logs</Typography>
                                <Typography fontSize="24px" fontWeight="800"> <em>{stats.counts?.find( item => +item._id === 200 ).count}</em> </Typography>
                            </Box>
                            <Box flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Error Logs</Typography>
                                <Typography fontSize="24px" fontWeight="800"> <em>{stats.counts?.reduce( ( sum, item ) => +item._id !== 200 ? sum + item.count : sum, 0 )}</em> </Typography>
                            </Box>
                            {stats.counts?.map( mod => {
                                const statusCode = mod._id
                                return +statusCode !== 200 ? (
                                    <Box minWidth="100px" key={statusCode} flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                        <Typography color="textSecondary" fontSize="12px" textTransform="capitalize" variant='subtitle2'><em>{statusCode + ""} Error</em></Typography>
                                        <Typography fontSize="24px" fontWeight="800"> <em>{mod.count}</em> </Typography>
                                    </Box>
                                ) : ""
                            } )}
                        </Box>}
                        {statsView && !error && loadingStats && <DataLoadingSpinner waitingMessage={"Loading error counts..."} />}
                    </Box>

                    {!error && !loading && <Box overflow="hidden" flexGrow={1} borderRadius={layout.borderRadius} justifyContent="space-between" border={border[1]} display="flex" gap={layout.cardsGap} flexDirection="column">
                        {!loading && !statsView && filteredLogs.length > 0 && <TableContainer sx={{ overflow: "auto", position: "relative" }}>
                            <Table stickyHeader>
                                <TableHeader columns={tableColumns} />
                                <TableBody>
                                    {filteredLogs.map( log => {
                                        return (
                                            <LogsTableRow key={log._id} log={log} />
                                        )
                                    } )}
                                </TableBody>
                            </Table>
                        </TableContainer>}

                        {/* CUSTOM PAGINATION */}
                        {!statsView && !loading && !error && filteredLogs.length > 0 && <Box display="flex" alignItems="center" justifyContent="space-between" gap={layout.cardsGap} bgcolor={palette.contentBg} padding="10px" >
                            <Typography fontSize="14px">{totalFetchedLogs} Fetched logs</Typography>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={layout.cardsGap}>
                                <Button disabled={loadingMore} startIcon={loadingMore ? <CircularProgress size={14} /> : Icons.default.DownloadIcon} variant='contained' disableElevation size='small' onClick={() => getData( true )} >Load more logs</Button>
                                <IconButton onClick={() => setPage( prev => prev - 1 )} disabled={( count * ( page - 1 ) + 1 ) <= count} size='small'>{Icons.default.KeyBoardArrowLeft}</IconButton>
                                <Typography fontSize="14px">{count * ( page - 1 ) + 1} to {totalFetchedLogs}</Typography>
                                <IconButton onClick={() => setPage( prev => prev + 1 )} disabled={( count * ( page ) ) >= totalFetchedLogs} size='small'>{Icons.default.KeyBoardArrowRight}</IconButton>
                            </Box>
                        </Box>}

                        {/* Stats Graph */}
                        {!loadingStats && statsView && <Card sx={{ flexShrink: 0, marginTop: "20px" }}>
                            <Typography padding="10px 20px" borderBottom={border[1]} variant='h6'>Errors Count By Module</Typography>
                            <Box flexGrow={1} height="400px" flexShrink={0} padding="20px 0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={stats.errorsByModule}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="shortCode" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill={palette.error.dark} activeBar={<Rectangle fill={palette.error.main} />} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Box display="flex" padding="0 20px 20px 20px" justifyContent="center" flexWrap="wrap" gap="10px">
                                {stats.errorsByModule.map( item => <Typography fontSize="12px" key={item._id}><strong>{item.shortCode}</strong>-<em>{item.module}</em> </Typography> )}
                            </Box>
                        </Card>}

                        {!statsView && !loading && !error && filteredLogs.length === 0 && < Box marginTop="20px" alignItems="center" display="flex" gap="10px" flexDirection="column" bgcolor={palette.contentBg} padding="40px 20px"  >
                            <Box component="img" width="180px" src={noLogsImg} />
                            <Typography variant="subtitle2">No logs to show{filtersApplied ? <em> for the applied filters</em> : ""}!</Typography>
                            {filtersApplied && <Button size='small' onClick={removeFilters} variant="outlined" color="greyed" sx={{ textTransform: "capitalize" }} startIcon={Icons.default.CloseIcon}>  <em> Remove Filters</em> </Button>}
                        </Box>}
                    </Box>}

                    {( ( loading && !statsView ) || ( statsView && loadingStats ) ) && !error && <DataLoadingSpinner padding='0' waitingMessage={statsView ? "Generating stats for the logs..." : "Fetching the logs please wait..."} />}

                    {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} />}
                </Box >
            </Box >
        ) :
            <Box padding={layout.pagePadding} flexGrow={1}>
                <BlockMessage message={<span>You doesn't have permission to access this page! please contact <i>Alive co-ordinators</i> for further information.</span>} type="error" />
            </Box>
    )
}

export default DebugLogs
